import { computed, ComputedRef, reactive, ref, Ref, UnwrapRef } from '@vue/composition-api';
import { SortInfo } from 'tdesign-vue';
import i18n, { i18n_t } from '@/locales/i18n';
import {
  FileApplyDetailData,
  FileApplyPageItem,
  FileApplyPageParams,
  FileApplyParams,
} from '@/renderer/scripts/libs/smh-sdk-class/typings';
import smh, { SMH } from '@/renderer/scripts/smh';
import tips from '@/renderer/scripts/utils/tips';
import store from '@/renderer/store';
import { fileApplyHandler } from '@/renderer/components/dialog/FileApply/composition/fileApplyHandler';
import useSort, { Sort } from '@/renderer/components/SpacePage/compositions/useSort';

// 列表类型(待审批,我的申请,历史审批)
export enum ApplyTableType {
  Applying,
  MyApply,
  HistoryApply,
}

// 待审批列表操作(查看,同意,拒绝,撤销,重新申请)
export enum ApplyOperate {
  View,
  Agree,
  Reject,
  Cancel,
  Re_Apply,
}

// 文件审批状态(审批中,已失效,已撤回,已驳回,审批通过)
export enum AuditStatus {
  Applying,
  InValid,
  Canceled,
  Rejected,
  Agreed,
}

// 文件审批失效(正常,文件被删除,空间被删除,用户被删除,角色被删除,群组被删)
export enum AuditStatusCause {
  Valid,
  FileDeleted,
  SpaceDeleted,
  UserDeleted,
  RoleDeleted,
  GroupDeleted,
}
type TableData = {
  totalNum: number;
  hasMore: boolean;
  loading: boolean;
  nextMarker: string | undefined;
  list: FileApplyPageItem[];
};
type DefaultParams = {
  type: 'my_audit' | 'my_apply';
  status: '' | 'auditing' | 'history';
  orderBy: 'creationTime' | 'operationTime';
};
export default function handler(type: Ref<ApplyTableType>) {
  const columns = computed(() => {
    let columnsArray = [
      { colKey: 'createdByNickname', title: i18n_t('申请人'), width: 160 },
      { colKey: 'directoryList', title: i18n_t('申请内容'), width: 300 },
      { colKey: 'applyNo', title: i18n_t('单号'), width: 160 },
      {
        colKey: 'creationTime',
        title: 'title-creationTime',
        titleDesc: i18n_t('申请时间'),
        sortType: 'all',
        sorter: true,
        width: 160,
        fixed: 'right',
      },
    ];
    const operatetTimeTitle = type.value === ApplyTableType.MyApply ? i18n_t('申请时间') : i18n_t('审批时间');
    const applyColkey = type.value === ApplyTableType.MyApply ? 'creationTime' : 'operationTime';
    switch (type.value) {
      case ApplyTableType.Applying:
        break;
      case ApplyTableType.MyApply:
      case ApplyTableType.HistoryApply:
        columnsArray = [
          {
            colKey: 'directoryList',
            title: i18n_t('申请内容'),
            width: 300,
          },
          {
            colKey: 'canAuditUsers',
            title: i18n_t('审批人'),
            width: 250,
          },
          {
            colKey: 'auditStatus',
            title: i18n_t('审批状态'),
            width: 160,
          },
          {
            colKey: 'applyNo',
            title: i18n_t('单号'),
            width: 160,
          },
          // @ts-ignore
          {
            colKey: applyColkey,
            title: `title-${applyColkey}`,
            titleDesc: operatetTimeTitle,
            sortType: 'all',
            sorter: true,
            width: 160,
          },
        ];
        columnsArray =
          type.value === ApplyTableType.HistoryApply
            ? [
                ...[
                  {
                    colKey: 'createdByNickname',
                    title: i18n_t('申请人'),
                    width: 160,
                  },
                ],
                ...columnsArray,
              ]
            : columnsArray;
        break;
      default:
        break;
    }
    return columnsArray;
  });
  const tableData = reactive<TableData>({
    totalNum: 0,
    loading: false,
    hasMore: false,
    nextMarker: '',
    list: [],
  });
  const {
    innerSort,
    tableSort,
    methods: sortMethods,
  } = useSort('fileApply', {
    sortBy: '',
    descending: true,
  });
  const loadingStatus = computed(() => {
    if (tableData.list.length > 0 && tableData.loading) {
      return 'loading';
    }
    if (tableData.hasMore) {
      return 'load-more';
    }
    return null;
  });
  const queryParams = reactive<FileApplyPageParams>({
    type: 'my_audit',
    marker: '',
    limit: 20,
    status: '',
  });

  // 默认c查询参数
  const defaultParams = computed(() => {
    let params: DefaultParams = {
      type: 'my_audit',
      status: 'auditing',
      orderBy: 'creationTime',
    };
    switch (type.value) {
      case ApplyTableType.Applying:
        break;
      case ApplyTableType.MyApply:
        params = {
          type: 'my_apply',
          status: '',
          orderBy: 'creationTime',
        };
        break;
      case ApplyTableType.HistoryApply:
        params = {
          type: 'my_audit',
          status: 'history',
          orderBy: 'operationTime',
        };
        break;
      default:
        break;
    }
    return params;
  });
  const fileApplyDetail = reactive<FileApplyDetailData>({
    spaceTag: '',
    applyNo: '',
    title: '',
    reason: '',
    canAuditUsers: [],
    auditStatus: 4,
    creationTime: '',
    createdBy: 0,
    createdByAvatar: '',
    createdByNickname: '',
    createdByCountryCode: '',
    createdByPhoneNumber: '',
    auditByAvatar: '',
    auditByNickname: '',
    operationTime: '',
    roleId: 0,
    directoryList: [],
    isAdmin: false,
    directoryPath: [],
    isCreatedByExist: false,
    auditStatusCause: 0,
    createByBelongLatestTeam: {
      id: 0,
      name: '',
      parentId: 0,
      spaceId: '',
      defaultRoleId: '',
      orgId: 0,
    },
    isUserBelongSpace: true,
    spaceId: '',
    relatedToMePath: null,
  });

  // 角色列表
  const roleMap = computed(() => {
    const roleMap: {
      [key: number]: string;
    } = {};
    store.getters.allRoles.forEach((role: SMH.Role) => {
      roleMap[role.id] = role.name;
    });
    return roleMap;
  });

  // 状态列表
  const auditStatusMap = [
    {
      theme: 'primary',
      label: i18n_t('待审批'),
    },
    {
      theme: 'default',
      label: i18n_t('已失效'),
    },
    {
      theme: 'default',
      label: i18n_t('已撤回'),
    },
    {
      theme: 'danger',
      label: i18n_t('已拒绝'),
    },
    {
      theme: 'success',
      label: i18n_t('已通过'),
    },
  ];
  const { fileApplyMethods, spacePath } = fileApplyHandler();
  const methods = {
    async pullList(loadMore?: true) {
      try {
        Object.assign(queryParams, defaultParams.value);
        // 避免重复加载同一个marker
        if (loadMore) {
          if (!tableData.hasMore || tableData.nextMarker === '') return;
          if (tableData.nextMarker === queryParams.marker) return;
          queryParams.marker = tableData.nextMarker;
        } else {
          tableData.loading = true;
        }
        const { data } = await smh.userAPI.getFileApplyList({
          ...queryParams,
          orderByType: innerSort.value.orderByType,
        });
        const { contents, totalNum, nextMarker } = data;
        Object.assign(tableData, {
          totalNum,
          nextMarker,
          list: loadMore ? [...tableData.list, ...contents] : contents,
          loading: false,
        });
        tableData.hasMore = tableData.list.length < tableData.totalNum;
      } catch (e: MsgError) {
        tips.error(e);
      } finally {
        tableData.loading = false;
      }
    },
    refresh() {
      // 重置查询条件
      Object.assign(tableData, {
        totalNum: 0,
        list: [],
      });
      Object.assign(queryParams, {
        marker: '',
        limit: 20,
        orderBy: 'creationTime',
        orderByType: 'desc',
      });
      methods.pullList();
    },
    async onOperate(applyNo: string, operate: ApplyOperate) {
      try {
        tableData.loading = true;
        switch (operate) {
          case ApplyOperate.View:
            await methods.getFileApplyDetail(applyNo);
            break;
          case ApplyOperate.Agree:
            await smh.userAPI.agreeFileApply({
              applyNo,
            });
            tips.success(i18n_t('审批通过'));
            break;
          case ApplyOperate.Reject:
            await smh.userAPI.rejectFileApply({
              applyNo,
            });
            tips.warning(i18n_t('审批已驳回'));
            break;
          case ApplyOperate.Cancel:
            await smh.userAPI.cancelFileApply({
              applyNo,
            });
            tips.success(i18n_t('已撤回'));
            break;
          case ApplyOperate.Re_Apply:
            // 暂时不做重新发起审批功能
            break;
          default:
            break;
        }
      } catch (e: MsgError) {
        tips.error(e);
      } finally {
        tableData.loading = false;
      }
    },
    async getFileApplyDetail(applyNo: string) {
      const { data } = await smh.userAPI.getFileApply({
        applyNo,
      });
      fileApplyMethods.analyseLocationInfo(data);
      Object.assign(fileApplyDetail, data);
    },
    async onAdd(params: FileApplyParams) {
      try {
        const { data } = await smh.userAPI.addFileApply(params);
        // todo 发起审批获取的结果有成功和失败项,需要具体处理判断
      } catch (e: MsgError) {
        tips.error(e);
      }
    },
    onScroll(params: { e: WheelEvent }) {
      if (tableData.loading) return;
      const { target } = params.e;
      const { clientHeight, scrollHeight, scrollTop } = target as HTMLElement;
      if (clientHeight + scrollTop >= scrollHeight - 100) {
        methods.pullList(true);
      }
    },
    switchSort(columnName: string) {
      // 升序→降序→无序→升序......
      methods.sortChange(sortMethods.switchSort(columnName));
    },
    sortChange(newSort: Sort | undefined) {
      sortMethods.sortChange(newSort);
      queryParams.marker = '';
      // 重置列表数据
      Object.assign(tableData, {
        totalNum: 0,
        list: [],
      });
      methods.pullList();
    },
  };
  return {
    spacePath,
    columns,
    tableData,
    sort: tableSort,
    loadingStatus,
    fileApplyDetail,
    roleMap,
    auditStatusMap,
    methods,
  };
}
